import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";

const LINKS = [
  { id: "home", label: "Home" },
  { id: "blueprint", label: "Blueprint" },
  { id: "capabilities", label: "Capabilities" },
  { id: "values", label: "Values" },
  { id: "leadership", label: "Leadership" },
  { id: "contact", label: "Contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("home");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-38% 0px -55% 0px" }
    );
    LINKS.forEach((l) => { const el = document.getElementById(l.id); if (el) obs.observe(el); });
    return () => { window.removeEventListener("scroll", onScroll); obs.disconnect(); };
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-navy/85 backdrop-blur-xl border-b border-gold/15" : "bg-transparent border-b border-transparent"
      }`}
    >
      <a
        href="#home"
        data-testid="skip-to-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:bg-gold focus:text-navy focus:px-4 focus:py-2 focus:font-mono focus:text-xs"
      >
        Skip to content
      </a>
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2 sm:px-6 lg:px-8">
        <a href="#home" data-testid="nav-logo" className="group flex items-center gap-3" aria-label="Vision-Heirs Enterprises — Home">
          <img
            src="/assets/vhe-logo.png"
            alt="Vision-Heirs Enterprises logo"
            className="h-20 w-auto drop-shadow-[0_0_18px_rgba(212,175,55,0.45)] transition-transform duration-500 group-hover:scale-[1.04] sm:h-24"
          />
          <span className="hidden font-serif text-lg font-semibold tracking-wide text-parchment md:block lg:text-xl">
            Vision-Heirs <span className="gold-text">Enterprises</span>
          </span>
        </a>

        <nav aria-label="Primary" className="hidden items-center gap-7 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`nav-link-${l.id}`}
              aria-current={active === l.id ? "true" : undefined}
              className={`relative font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                active === l.id ? "text-gold" : "text-slate-300 hover:text-goldlight"
              }`}
            >
              {l.label}
              <span
                className={`absolute -bottom-1.5 left-1/2 h-px -translate-x-1/2 bg-gold transition-[width] duration-400 ${
                  active === l.id ? "w-full" : "w-0"
                }`}
              />
            </a>
          ))}
          <a
            href="#contact"
            data-testid="nav-connect-button"
            className="group ml-2 inline-flex items-center gap-2 rounded-full border border-gold/60 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.22em] text-gold transition-colors duration-300 hover:bg-gold hover:text-navy"
          >
            Connect
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </nav>

        <button
          type="button"
          data-testid="nav-mobile-toggle"
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? "Close navigation menu" : "Open navigation menu"}
          onClick={() => setOpen((v) => !v)}
          className="rounded-md border border-gold/40 p-2 text-gold lg:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            id="mobile-menu"
            aria-label="Mobile"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-gold/15 bg-navy/95 backdrop-blur-xl lg:hidden"
          >
            <div className="space-y-1 px-6 py-6">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.id}
                  href={`#${l.id}`}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  className="block py-2.5 font-serif text-2xl text-parchment transition-colors hover:text-gold"
                >
                  <span className="mr-3 font-mono text-[10px] tracking-[0.25em] text-gold/70">0{i + 1}</span>
                  {l.label}
                </motion.a>
              ))}
              <a
                href="#contact"
                data-testid="nav-mobile-connect-button"
                onClick={() => setOpen(false)}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-navy"
              >
                Connect <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
